import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useSearchParams, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

// Critical homepage components loaded eagerly (above the fold)
import { Navbar } from './features/home/components/Navbar';
import { HeroSection } from './features/home/components/HeroSection';
import { Footer } from './features/home/components/Footer';

// Hooks & UI
import { useHomepage } from './hooks/useHomepage';
import { Loader } from './components/ui/Loader';
import { GuestProvider } from './providers/GuestProvider';
import { AiChatWidget } from './features/ai-agent/components/AiChatWidget';
import { PromoPopupWrapper } from './core/components/PromoPopupWrapper';

import './index.css';

// ─────────────────────────────────────────────────
// LAZY-LOADED: Home sections (below-the-fold)
// ─────────────────────────────────────────────────
const DiscoveryLayer = lazy(() =>
    import('./features/home/components/DiscoveryLayer').then(m => ({ default: m.DiscoveryLayer }))
);
const BlockRenderer = lazy(() =>
    import('./features/home/components/BlockRenderer').then(m => ({ default: m.BlockRenderer }))
);

// ─────────────────────────────────────────────────
// LAZY-LOADED: Public pages
// ─────────────────────────────────────────────────
const LensGuidePage = lazy(() =>
    import('./features/lens-guide/LensGuidePage').then(m => ({ default: m.LensGuidePage }))
);
const PaymentCallbackPage = lazy(() =>
    import('./features/payment/pages/PaymentCallbackPage').then(m => ({ default: m.PaymentCallbackPage }))
);
const ProductPage = lazy(() =>
    import('./features/product/ProductPage').then(m => ({ default: m.ProductPage }))
);
const WishlistPage = lazy(() =>
    import('./features/wishlist/WishlistPage').then(m => ({ default: m.WishlistPage }))
);
const CheckoutPage = lazy(() =>
    import('./features/checkout/CheckoutPage').then(m => ({ default: m.CheckoutPage }))
);
const OrderConfirmationPage = lazy(() =>
    import('./features/checkout/OrderConfirmationPage').then(m => ({ default: m.OrderConfirmationPage }))
);
const PublicOrdersPage = lazy(() =>
    import('./features/orders/OrdersPage').then(m => ({ default: m.OrdersPage }))
);
const PublicOrderDetailsPage = lazy(() =>
    import('./features/orders/OrderDetailsPage').then(m => ({ default: m.OrderDetailsPage }))
);
const BrandPage = lazy(() =>
    import('./features/brand/BrandPage').then(m => ({ default: m.BrandPage }))
);
const CategoryPage = lazy(() =>
    import('./features/category/CategoryPage').then(m => ({ default: m.CategoryPage }))
);
const AllCategoriesPage = lazy(() =>
    import('./features/category/AllCategoriesPage').then(m => ({ default: m.AllCategoriesPage }))
);
const ShopPage = lazy(() =>
    import('./features/shop/ShopPage').then(m => ({ default: m.ShopPage }))
);

const ShopRedirect = () => {
    const [searchParams] = useSearchParams();
    const sort = searchParams.get('sort');
    const tag = searchParams.get('tag');

    if (sort === 'trending') {
        return <Navigate to="/shop/trending" replace />;
    }
    if (tag === 'try-on') {
        return <Navigate to="/shop/try-on" replace />;
    }
    return <Navigate to="/" replace />;
};

const SearchPage = lazy(() =>
    import('./features/search/SearchPage').then(m => ({ default: m.SearchPage }))
);
const BlogListPage = lazy(() =>
    import('./features/blog/pages/BlogListPage').then(m => ({ default: m.BlogListPage }))
);
const BlogPostPage = lazy(() =>
    import('./features/blog/pages/BlogPostPage').then(m => ({ default: m.BlogPostPage }))
);
const BranchesMapPage = lazy(() =>
    import('./features/branch/BranchesMapPage').then(m => ({ default: m.BranchesMapPage }))
);
const OpticalCentersMapPage = lazy(() =>
    import('./features/optical-centers/OpticalCentersMapPage').then(m => ({ default: m.OpticalCentersMapPage }))
);
const PrivacyPolicyPage = lazy(() =>
    import('./features/pages/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage }))
);
const TermsConditionsPage = lazy(() =>
    import('./features/pages/TermsConditionsPage').then(m => ({ default: m.TermsConditionsPage }))
);
const ReturnPolicyPage = lazy(() =>
    import('./features/pages/ReturnPolicyPage').then(m => ({ default: m.ReturnPolicyPage }))
);
const DeliveryPolicyPage = lazy(() =>
    import('./features/pages/DeliveryPolicyPage').then(m => ({ default: m.DeliveryPolicyPage }))
);
const ContactPage = lazy(() =>
    import('./features/pages/ContactPage').then(m => ({ default: m.ContactPage }))
);

// ─────────────────────────────────────────────────
// LAZY-LOADED: Auth & account pages
// ─────────────────────────────────────────────────
const LoginPage = lazy(() =>
    import('./features/auth/pages/LoginPage').then(m => ({ default: m.LoginPage }))
);
const RegisterPage = lazy(() =>
    import('./features/auth/pages/RegisterPage').then(m => ({ default: m.RegisterPage }))
);
const ForgotPasswordPage = lazy(() =>
    import('./features/auth/pages/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage }))
);
const ResetPasswordPage = lazy(() =>
    import('./features/auth/pages/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage }))
);
const AccountPage = lazy(() =>
    import('./features/account/AccountPage').then(m => ({ default: m.AccountPage }))
);
const TicketDetailPage = lazy(() =>
    import('./features/account/TicketDetailPage').then(m => ({ default: m.TicketDetailPage }))
);

// ─────────────────────────────────────────────────
// LAZY-LOADED: Error pages
// ─────────────────────────────────────────────────
const NotFoundPage = lazy(() =>
    import('./features/errors/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage }))
);
const ServerErrorPage = lazy(() =>
    import('./features/errors/pages/ServerErrorPage').then(m => ({ default: m.ServerErrorPage }))
);

// ─────────────────────────────────────────────────
// LAZY-LOADED: Admin — entire admin dashboard chunk
// ─────────────────────────────────────────────────
const AdminLayout = lazy(() =>
    import('./features/admin/components/AdminLayout').then(m => ({ default: m.AdminLayout }))
);
const ProtectedRoute = lazy(() =>
    import('./features/admin/components/ProtectedRoute').then(m => ({ default: m.ProtectedRoute }))
);
const AdminLoginPage = lazy(() =>
    import('./features/admin/pages/AdminLoginPage').then(m => ({ default: m.AdminLoginPage }))
);
const DashboardPage = lazy(() =>
    import('./features/admin/pages/DashboardPage').then(m => ({ default: m.DashboardPage }))
);
const LiveEventsPage = lazy(() =>
    import('./features/admin/pages/LiveEventsPage').then(m => ({ default: m.LiveEventsPage }))
);
const UsersPage = lazy(() =>
    import('./features/admin/pages/UsersPage').then(m => ({ default: m.UsersPage }))
);
const UserDetailPage = lazy(() =>
    import('./features/admin/pages/UserDetailPage').then(m => ({ default: m.UserDetailPage }))
);
const AdminsPage = lazy(() =>
    import('./features/admin/pages/AdminsPage').then(m => ({ default: m.AdminsPage }))
);
const HomepageCmsPage = lazy(() =>
    import('./features/admin/pages/HomepageCmsPage/HomepageCmsPage').then(m => ({ default: m.HomepageCmsPage }))
);
const SettingsPage = lazy(() =>
    import('./features/admin/pages/SettingsPage').then(m => ({ default: m.SettingsPage }))
);
const OtpSettingsPage = lazy(() =>
    import('./features/admin/pages/SettingsPage/OtpSettingsPage').then(m => ({ default: m.OtpSettingsPage }))
);

const InvoiceSettingsPage = lazy(() =>
    import('./features/admin/pages/SettingsPage/InvoiceSettingsPage').then(m => ({ default: m.InvoiceSettingsPage }))
);
const PaymentSettingsPage = lazy(() =>
    import('./features/admin/pages/SettingsPage/PaymentSettingsPage').then(m => ({ default: m.PaymentSettingsPage }))
);
const PromoSettingsPage = lazy(() =>
    import('./features/admin/pages/PromoSettingsPage').then(m => ({ default: m.PromoSettingsPage }))
);
const CategoriesPage = lazy(() =>
    import('./features/admin/pages/CategoriesPage').then(m => ({ default: m.CategoriesPage }))
);
const ProductsPage = lazy(() =>
    import('./features/admin/pages/ProductsPage').then(m => ({ default: m.ProductsPage }))
);
const ProductFormPage = lazy(() =>
    import('./features/admin/pages/ProductFormPage').then(m => ({ default: m.ProductFormPage }))
);
const BrandsPage = lazy(() =>
    import('./features/admin/pages/BrandsPage').then(m => ({ default: m.BrandsPage }))
);
const GlassesModelsPage = lazy(() =>
    import('./features/admin/pages/GlassesModelsPage').then(m => ({ default: m.GlassesModelsPage }))
);
const FrameShapesPage = lazy(() =>
    import('./features/admin/pages/FrameShapesPage').then(m => ({ default: m.FrameShapesPage }))
);
const FrameTypesPage = lazy(() =>
    import('./features/admin/pages/FrameTypesPage').then(m => ({ default: m.FrameTypesPage }))
);
const ReviewsPage = lazy(() =>
    import('./features/admin/pages/ReviewsPage/ReviewsPage').then(m => ({ default: m.ReviewsPage }))
);
const ShippingZonesPage = lazy(() =>
    import('./features/admin/pages/ShippingZonesPage/ShippingZonesPage').then(m => ({ default: m.ShippingZonesPage }))
);
const BranchesAdminPage = lazy(() => import('./features/admin/pages/BranchesAdminPage'));
const BranchFormPage = lazy(() => import('./features/admin/pages/BranchFormPage'));
const OpticalCentersAdminPage = lazy(() => import('./features/admin/pages/OpticalCentersAdminPage'));
const OpticalCenterFormPage = lazy(() => import('./features/admin/pages/OpticalCenterFormPage'));
const ReturnsPage = lazy(() => import('./features/admin/pages/ReturnsPage/ReturnsPage'));
const OrdersPage = lazy(() => import('./features/admin/pages/OrdersPage/OrdersPage'));
const CouponsPage = lazy(() => import('./features/admin/pages/CouponsPage/CouponsPage'));
const CartsPage = lazy(() => import('./features/admin/pages/CartsPage/CartsPage'));
const WishlistsPage = lazy(() => import('./features/admin/pages/WishlistsPage/WishlistsPage'));
const NotificationsPage = lazy(() =>
    import('./features/admin/pages/NotificationsPage').then(m => ({ default: m.NotificationsPage }))
);
const SendNotificationPage = lazy(() =>
    import('./features/admin/pages/SendNotificationPage').then(m => ({ default: m.SendNotificationPage }))
);
const BlogPage = lazy(() =>
    import('./features/admin/pages/BlogPage/BlogPage').then(m => ({ default: m.BlogPage }))
);
const BlogFormPage = lazy(() =>
    import('./features/admin/pages/BlogPage/BlogFormPage').then(m => ({ default: m.BlogFormPage }))
);
const LensProductsPage = lazy(() =>
    import('./features/admin/pages/LensProductsPage').then(m => ({ default: m.LensProductsPage }))
);
const LensProductFormPage = lazy(() =>
    import('./features/admin/pages/LensProductFormPage').then(m => ({ default: m.LensProductFormPage }))
);
const PrescriptionRulesPage = lazy(() =>
    import('./features/admin/pages/PrescriptionRulesPage').then(m => ({ default: m.PrescriptionRulesPage }))
);
const PrescriptionRuleFormPage = lazy(() =>
    import('./features/admin/pages/PrescriptionRuleFormPage').then(m => ({ default: m.PrescriptionRuleFormPage }))
);
const CatalystPage = lazy(() =>
    import('./features/admin/pages/CatalystPage/CatalystPage').then(m => ({ default: m.CatalystPage }))
);
const CrashReportDetail = lazy(() =>
    import('./features/admin/pages/CatalystPage/CrashReportDetail').then(m => ({ default: m.CrashReportDetail }))
);
const CatalystSettings = lazy(() =>
    import('./features/admin/pages/CatalystPage/CatalystSettings').then(m => ({ default: m.CatalystSettings }))
);
const LeadsPage = lazy(() =>
    import('./features/admin/pages/LeadsPage/LeadsPage').then(m => ({ default: m.LeadsPage }))
);
const TicketsAdminPage = lazy(() =>
    import('./features/admin/pages/TicketsPage/TicketsAdminPage').then(m => ({ default: m.TicketsAdminPage }))
);
const TicketDetailAdmin = lazy(() =>
    import('./features/admin/pages/TicketsPage/TicketDetailAdmin').then(m => ({ default: m.TicketDetailAdmin }))
);

// ─────────────────────────────────────────────────
// Shared fallback spinner for Suspense
// ─────────────────────────────────────────────────
const PageLoader = () => (
    <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <Loader size="lg" />
    </div>
);

// ─────────────────────────────────────────────────
// Scroll To Top on Route Change
// ─────────────────────────────────────────────────
function ScrollToTop() {
    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    return null;
}

function LandingPage() {
    const { data: homepageData, isLoading } = useHomepage();

    useEffect(() => {
        if (homepageData?.metadata) {
            if (homepageData.metadata.title) {
                document.title = homepageData.metadata.title;
            }

            if (homepageData.metadata.description) {
                let metaDesc = document.querySelector('meta[name="description"]');
                if (metaDesc) {
                    metaDesc.setAttribute('content', homepageData.metadata.description);
                } else {
                    const meta = document.createElement('meta');
                    meta.name = 'description';
                    meta.content = homepageData.metadata.description;
                    document.head.appendChild(meta);
                }
            }
        }
    }, [homepageData]);

    if (isLoading) {
        return <PageLoader />;
    }

    return (
        <div className="min-h-screen bg-background font-sans text-text-main selection:bg-primary selection:text-white">
            <Navbar />
            <HeroSection />
            {/* Below-the-fold sections are lazy-loaded */}
            <Suspense fallback={<div className="min-h-[400px]" />}>
                <DiscoveryLayer />
                <BlockRenderer blocks={homepageData?.sections || []} />
            </Suspense>
            <Footer />
        </div>
    );
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: 1,
            refetchOnWindowFocus: false,
        },
    },
});

export default function App() {
    const { i18n } = useTranslation();

    useEffect(() => {
        document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
        document.documentElement.lang = i18n.language;
    }, [i18n.language]);

    return (
        <QueryClientProvider client={queryClient}>
            <BrowserRouter>
                <ScrollToTop />
                <Toaster
                    position="top-right"
                    toastOptions={{
                        className: 'font-sans text-sm',
                        success: { duration: 3000, iconTheme: { primary: '#10B981', secondary: '#fff' } },
                        error: { duration: 4000, iconTheme: { primary: '#EF4444', secondary: '#fff' } },
                    }}
                />
                <GuestProvider>
                    <Suspense fallback={<PageLoader />}>
                        <Routes>
                            {/* Public Landing Page */}
                            <Route path="/" element={<LandingPage />} />
                            <Route path="/search" element={<SearchPage />} />
                            <Route path="/product/:slug" element={<ProductPage />} />
                            <Route path="/wishlist" element={<WishlistPage />} />
                            <Route path="/brand/:slug" element={<BrandPage />} />
                            <Route path="/shop" element={<ShopRedirect />} />
                            <Route path="/shop/:collection" element={<ShopPage />} />
                            <Route path="/categories" element={<AllCategoriesPage />} />
                            <Route path="/category/:slug" element={<CategoryPage />} />
                            <Route path="/checkout" element={<CheckoutPage />} />
                            <Route path="/lens-guide" element={<LensGuidePage />} />
                            <Route path="/order/:id/confirmation" element={<OrderConfirmationPage />} />
                            <Route path="/orders" element={<PublicOrdersPage />} />
                            <Route path="/orders/:id" element={<PublicOrderDetailsPage />} />
                            <Route path="/blog" element={<BlogListPage />} />
                            <Route path="/blog/:slug" element={<BlogPostPage />} />
                            <Route path="/branches" element={<BranchesMapPage />} />
                            <Route path="/optical-centers" element={<OpticalCentersMapPage />} />
                            <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
                            <Route path="/legal/terms" element={<TermsConditionsPage />} />
                            <Route path="/legal/returns" element={<ReturnPolicyPage />} />
                            <Route path="/legal/delivery" element={<DeliveryPolicyPage />} />
                            <Route path="/contact" element={<ContactPage />} />

                            {/* Payment Gateway Callbacks */}
                            <Route path="/payment/callback" element={<PaymentCallbackPage />} />

                            {/* Customer Auth & Account */}
                            <Route path="/auth/login" element={<LoginPage />} />
                            <Route path="/auth/register" element={<RegisterPage />} />
                            <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
                            <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
                            <Route path="/account" element={<AccountPage />} />
                            <Route path="/account/tickets/:id" element={<TicketDetailPage />} />

                            {/* Admin Portal Auth */}
                            <Route path="/admin/login" element={<AdminLoginPage />} />

                            {/* Protected Admin Routes */}
                            <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                                <Route index element={<DashboardPage />} />
                                <Route path="blog" element={<BlogPage />} />
                                <Route path="blog/new" element={<BlogFormPage />} />
                                <Route path="blog/:id" element={<BlogFormPage />} />
                                <Route path="live-events" element={<LiveEventsPage />} />
                                <Route path="orders" element={<OrdersPage />} />
                                <Route path="coupons" element={<CouponsPage />} />
                                <Route path="carts" element={<CartsPage />} />
                                <Route path="wishlists" element={<WishlistsPage />} />
                                <Route path="products" element={<ProductsPage />} />
                                <Route path="products/new" element={<ProductFormPage />} />
                                <Route path="products/:id" element={<ProductFormPage />} />
                                <Route path="categories" element={<CategoriesPage />} />
                                <Route path="brands" element={<BrandsPage />} />
                                <Route path="glasses-models" element={<GlassesModelsPage />} />
                                <Route path="frame-shapes" element={<FrameShapesPage />} />
                                <Route path="frame-types" element={<FrameTypesPage />} />
                                <Route path="lens-products" element={<LensProductsPage />} />
                                <Route path="lens-products/new" element={<LensProductFormPage />} />
                                <Route path="lens-products/:id" element={<LensProductFormPage />} />
                                <Route path="prescription-rules" element={<PrescriptionRulesPage />} />
                                <Route path="prescription-rules/new" element={<PrescriptionRuleFormPage />} />
                                <Route path="prescription-rules/:id" element={<PrescriptionRuleFormPage />} />
                                <Route path="shipping-zones" element={<ShippingZonesPage />} />
                                <Route path="branches" element={<BranchesAdminPage />} />
                                <Route path="branches/new" element={<BranchFormPage />} />
                                <Route path="branches/:id" element={<BranchFormPage />} />
                                <Route path="optical-centers" element={<OpticalCentersAdminPage />} />
                                <Route path="optical-centers/new" element={<OpticalCenterFormPage />} />
                                <Route path="optical-centers/:id" element={<OpticalCenterFormPage />} />
                                <Route path="returns" element={<ReturnsPage />} />
                                <Route path="reviews" element={<ReviewsPage />} />
                                <Route path="notifications" element={<NotificationsPage />} />
                                <Route path="send-notification" element={<SendNotificationPage />} />
                                <Route path="users" element={<UsersPage />} />
                                <Route path="users/:id" element={<UserDetailPage />} />
                                <Route path="admins" element={<AdminsPage />} />
                                <Route path="cms/homepage" element={<HomepageCmsPage />} />
                                <Route path="leads" element={<LeadsPage />} />
                                <Route path="settings" element={<SettingsPage />} />

                                <Route path="settings/invoice" element={<InvoiceSettingsPage />} />
                                <Route path="settings/payment" element={<PaymentSettingsPage />} />
                                <Route path="settings/promo" element={<PromoSettingsPage />} />
                                <Route path="settings/otp" element={<OtpSettingsPage />} />

                                <Route path="catalyst" element={<CatalystPage />} />
                                <Route path="catalyst/crashes/:id" element={<CrashReportDetail />} />
                                <Route path="catalyst/settings" element={<CatalystSettings />} />

                                <Route path="tickets" element={<TicketsAdminPage />} />
                                <Route path="tickets/:id" element={<TicketDetailAdmin />} />
                            </Route>

                            {/* Error Pages */}
                            <Route path="/500" element={<ServerErrorPage />} />
                            <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                        <PromoPopupWrapper />
                        <AiChatWidget />
                    </Suspense>
                </GuestProvider>
            </BrowserRouter>
        </QueryClientProvider>
    );
}
